import * as React from 'react';
import LocalDiv from '../localization/LocalDiv';
import { css } from '@emotion/core';
import SurveyHeader from './survey/SurveyHeader';
import { FLEX_ADAPTIVE, FLEX_COLUMN } from '../layout/flexBox';
import ClamShell from '../layout/ClamShell';
import RaterStatusCard from './survey/raters/raterSummary/RaterStatusCard';
import Spacer from '../layout/Spacer';
import ProjectSteps from './ProjectSteps';
import ActionMsg from '../localization/ActionMsg';
import { Str } from '../localization/translationKeys';
import ProjectResources from './resources/ProjectResources';
import DevPlanSummary from './devPlan/DevPlanSummary';
import { Link, useHistory } from 'react-router-dom';
import { useRoutes } from '../navigation/routes';
import ProjectLayout from './ProjectLayout';
import { onMobileDevice } from '../globalStyle/mediaQueries';
import { useMsgText } from '../localization/hooks';
import { useAppState } from '../core/redux/hooks';
import { useProjIds } from '../navigation/hooks';
import ErrorMsg from '../error/ErrorMsg';
import ReassessmentModal from './survey/ReassessmentModal';
import { useToggle } from '../core/hooks/useToggle';
import { useDispatch } from 'react-redux';
import { reassessmentAction } from './api/reassessmentAction';
import { preEnrollECourseAction, resetPreEnrollECourseAction } from './api/preEnrollECourseAction';
import { isNilOrEmpty } from '../language/module';
import { useEffect, useMemo, useRef } from 'react';
import Card from '../layout/card/Card';
import Msg from '../localization/Msg';
import { btnStyle } from '../forms/styles/button';
import { COLORS } from '../globalStyle/colors';


const styles = {
  devPlan:         css`
    flex-grow: 1;
    flex-basis: 1px;
  `,
  customResources: css`
    flex-basis: 1px;
    flex-grow: 1;
  `,
  header:          css`
    margin-bottom: 40px;
    
    ${onMobileDevice} {
      margin-bottom: 35px;
    }
  `,
  actionBanner:    css`
    padding-bottom: 20px;
  `,
  projectSteps:    css`
    & > a { 
      margin-top: 80px; 
    }
  `,
  card:            css`
    background-color: #20395a;
    color: white;
    padding: 30px;
    margin-bottom: 20px;  
  `,
  content:      css`
    justify-content: space-between;
  `,
  cardLeft:      css`
    padding-left: 0;
    padding-right: 0;
    border-left: none;
    border-right: none;

    &[dir="ltr"] {
      padding-right: 30px;
      border-right: 1px solid ${COLORS.LIGHT_GREY};
    }
    
    &[dir="rtl"] {
      padding-left: 30px;
      border-left: 1px solid ${COLORS.LIGHT_GREY};
    }
    
    ${onMobileDevice} {
      border: none !important;
    }
  `,
  cardRight:      css`
    margin: 0 18px;
    align-items: center;
    justify-content: center;
    padding-left: 0;
    padding-right: 0;

    &[dir="ltr"] {
      padding-left: 20px;
    }
    
    &[dir="rtl"] {
      padding-right: 20px;
    }
  `,  
  cardTitle:            css`
    font-size: 22px; 
    font-weight: bold; 
    color: white;
  `,
  cardDescription:  css`
    padding-top: 10px;
    line-height: 16px;
    
    &[dir="ltr"] {
      padding-left: 4%;
    }
    
    &[dir="rtl"] {
      padding-right: 4%;
    }
    
    ${onMobileDevice} { 
      margin-bottom: 40px;
      padding: 0 !important;
    }
  `,
  cardPercent:    css`
    font-size: 36px;
    font-weight: bold;
    margin-bottom: 2px;
    text-align: center;
  `,
  cardCompleted:    css`
    font-size: 12px;
    font-weight: bold;
    margin-bottom: 20px;
    text-align: center;
  `,
  courseLink: btnStyle.primary(COLORS.GREEN),
  btn :            css`
    display: block;
    min-width: 152px;
  `,
  linkDisabled:       css`
    cursor: default;
    opacity: .6;

    &:hover { 
      text-decoration: none;
      cursor: default;
    }
  `,  
};

type Props = {
  className?: string;
};


/**
 * ProjectSummary: a component that renders a basic overview of a project's status plus links out to the various bits
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ProjectSummary: React.FC<Props> = props => {
  const history = useHistory();
  const dispatch = useDispatch();

  const projIds = useProjIds();

  const pageTitle = useMsgText(Str.Dashboard);

  const project   = useAppState(state => state.projectSummary);
  const resources = useAppState(state => state.projectResources);
  const projReqs  = useAppState(state => state.participantRequirements || []);

  const routes = useRoutes(projIds.participantId, projIds.projDisplayId);

  const showSurveyBanner = !project?.expired 
                           && !!project?.followupSurveyStep
                           && !project?.followupSurveyCreated
                           && project?.courseAllowCreateSurvey;
  const showInviteBanner = !showSurveyBanner
                           && !project?.expired
                           && project?.inviteRatersStep
                           && !project?.raterInvited
                           && !project.surveyPastDue
                           && project?.courseAllowInviteRaters;
  const showProjectSteps = project?.selfSurveyStep || project?.setGoalsStep || project?.viewReportStep;
  const showRaterStatus = project?.inviteRatersStep;
  const showReassessmentBanner = project?.reassessmentProject 
                                 && project?.reassessmentTriggerOwn 
                                 && project?.reportAvailable 
                                 && (!project?.hasReassessmentRater || !project.reassessmentSurveyCompleted)
  const showCourseworkCard = project?.courseworkAbove || project?.courseworkBelow;
  const showCompletionPercent = project?.courseCompletionPct! > -1;

  const [confirmReassessment, toggleConfirmReassessment] = useToggle();

  const onCreateSurveyClick = React.useCallback(
    () => history.push(routes.CreateSurvey),
    [history, routes.CreateSurvey],
  );

  const onInviteRatersClick = React.useCallback(
    () => history.push(routes.InviteRaters),
    [history, routes.InviteRaters],
  );

  const reassessmentSurveyUrl = useAppState(state => state.reassessmentSurveyUrl);

  const onConfirmReassessmentClick = React.useCallback(
    () => {
      dispatch(reassessmentAction(projIds.projDisplayId, projIds.participantId));
      // close the modal
      toggleConfirmReassessment();
    },
    [projIds.projDisplayId, projIds.participantId, dispatch, toggleConfirmReassessment],
  );

  /* memoize project settings */
  const courseNotStarted = useMemo(() => project?.courseCompletionPct === 0, [project]);
  const courseCompleted  = useMemo(() => project?.courseCompletionPct === 100, [project]);
  const courseHasCompletion  = useMemo(() => project?.courseCompletionPct! >= 0, [project]);

  const menuText  = useMemo(() => 
    !courseHasCompletion
      ? Str.CourseworkAccess
      : courseNotStarted
        ? Str.CourseworkBegin 
        : courseCompleted
          ? Str.CourseworkCompleted
          : Str.CourseworkContinue,
    [courseNotStarted, courseCompleted, courseHasCompletion],
  );

  const completedText  = useMemo(() => 
    project?.courseCompletionPct + '%',
    [project],
  );

  const availableAfter = project?.courseAvailableAfter ?? 0;

  const courseDisabled = useMemo(
    () => {
      if (availableAfter === 1)
        return !project?.followupSurveyCreated;
      else if (availableAfter === 2)
        return !project?.ratersCompleted;
      else if (availableAfter === 3)
        return !project?.selfSurveyCompleted;
      else if (availableAfter === 4)
        return !project?.setGoalsCompleted;
      else if (availableAfter === 5)
        return !project?.reportAvailable;
      else if (availableAfter === 6)
        return !project?.devPlanCompleted;;
    },
    [availableAfter, project],
  );

  useEffect(
    () => {
      // open survey in new tab if successful
      if (!isNilOrEmpty(reassessmentSurveyUrl)) {
        window.open(reassessmentSurveyUrl, '_self');
      }
    },
    [reassessmentSurveyUrl],
  );
  // may also want to use useOnSuccess

  // Pre-enroll the participant in the eCourse so the Launch click can skip IsEnrolled.
  // Reset on project/participant change and on unmount; only fire once per (project, participant)
  // so ProjectStatusPoller ticks (which update projectSummary) don't cause re-fires.
  const preEnrolledRef = useRef(false);

  useEffect(
    () => {
      preEnrolledRef.current = false;
      return () => {
        dispatch(resetPreEnrollECourseAction());
      };
    },
    [projIds.projId, projIds.participantId, dispatch],
  );

  useEffect(
    () => {
      if (
        project
        && !courseDisabled
        && !preEnrolledRef.current
        && projIds.projId
        && projIds.participantId
        && projIds.projId >= 0
        && projIds.participantId >= 0
      ) {
        preEnrolledRef.current = true;
        dispatch(preEnrollECourseAction(projIds.projId, projIds.participantId));
      }
    },
    [project, courseDisabled, projIds.projId, projIds.participantId, dispatch],
  );

  if (!project) {
    return <ErrorMsg error={Str.LoadFailed} />;
  }

  return (
    <ProjectLayout>
      <SurveyHeader
        title={pageTitle}
        deadline={new Date(project.surveyDeadline ?? new Date())}
        css={styles.header}
      />

      { // Coursework Card
        (showCourseworkCard) && (
          <Card css={styles.card} className={props.className}>
            <div css={[FLEX_ADAPTIVE, styles.content]}>
              <LocalDiv css={[FLEX_COLUMN, styles.cardLeft]} className={props.className}>
                {/* title */}
                <div css={styles.cardTitle}>
                  {project?.courseTitle}
                </div>
                {/* description */}
                <div css={styles.cardDescription}>
                  {project?.courseDescription}
                </div>
              </LocalDiv>
              <LocalDiv css={[FLEX_COLUMN, styles.cardRight]} className={props.className}>
                {
                  (showCompletionPercent) && (
                    <>
                      {/* completion percent */}
                      <div css={styles.cardPercent}>
                        {completedText}
                      </div>
                      <div css={styles.cardCompleted}>
                        <Msg str={Str.Completed} />
                      </div>
                    </>
                  )
                }
                {/* button */}
                <div>
                  <Link key='eCourse'
                    to={courseDisabled ? '#' : routes.ECourse}
                    css={[styles.courseLink, styles.btn, courseDisabled && styles.linkDisabled]}
                  >
                    <Msg str={menuText} defaultMsg={'[default]'} />
                  </Link>
                </div>
              </LocalDiv> 
            </div> 
          </Card>
        )
      }

      { // Invite Raters Banner
        <ClamShell show={showInviteBanner}>
          <div css={styles.actionBanner}>
            <ActionMsg
              msg={Str.DashInviteMsg}
              showBtn={true}
              btnMsg={Str.InviteRaters}
              onClick={onInviteRatersClick}
            />
          </div>
        </ClamShell>
      }
      { // Create Survey Banner
        <ClamShell show={showSurveyBanner}>
          <div css={styles.actionBanner}>
            <ActionMsg
              msg={Str.DashCreateSurveyMsg}
              showBtn={true}
              btnMsg={Str.CreateASurvey}
              onClick={onCreateSurveyClick}
            />
          </div>
        </ClamShell>
      }
      { // Reassessment Banner
        <ClamShell show={showReassessmentBanner}>
          <div css={styles.actionBanner}>
            <ActionMsg
              msg={Str.ReassessmentMsg}
              showBtn={true}
              btnMsg={Str.RetakeAssessment}
              onClick={toggleConfirmReassessment}
            />
          </div>
        </ClamShell>
      }
      {
        confirmReassessment && (
          <ReassessmentModal
            onClickYes={onConfirmReassessmentClick}
            onDismiss={toggleConfirmReassessment}
          />
        )
      }

      {/* Add messaging clamshell here - will need mechanism for when and what to show */}

      {
        (showRaterStatus) && (
          <RaterStatusCard
            routes={routes}
            proj={project}
            requirements={projReqs}
          />
        )
      }

      {
        (showProjectSteps) && (
          <ProjectSteps
            routes={routes}
            proj={project}
            css={css`& > a { margin-top: 80px; }`}
          />
        )
      }

      {/* Development Plan & Project Resources */}
      <LocalDiv css={[FLEX_ADAPTIVE, css`margin-top: 80px;`]}>
        {
          (project.developmentPlanStep) && (
            <>
              <DevPlanSummary routes={routes} plan={project} projectId={project.projectId} css={styles.devPlan} />
              <Spacer />
            </>
          )
        }
        <ProjectResources
          routes={routes}
          resources={resources}
          css={styles.customResources}
        />
      </LocalDiv>
    </ProjectLayout>
  );
};

export default ProjectSummary;
